// import gsap from 'gsap'
import modalContent from 'components/modal/modal-content'

interface ModalExtras {
  imageSrc?: string
  desktopString?: string
  mobileString?: string
  culture?: string
  arUrl?: string
  id?: string
  line?: string
  swiper?: HTMLElement
}

const modal = {
  el: {
    root: document.querySelector<HTMLElement>('.modal'),
    overlay: document.querySelector<HTMLElement>('.modal__overlay'),
    modals: document.querySelectorAll<HTMLElement>('.modal'),
    body: document.querySelector<HTMLElement>('.body'),
    close: document.querySelector<HTMLButtonElement>('.modal__close'),
    content: document.querySelector<HTMLElement>('.modal__content'),
    loader: document.querySelector<HTMLElement>('.modal__loader'),
    next: document.querySelector<HTMLButtonElement>('.modal-button--next'),
    prev: document.querySelector<HTMLButtonElement>('.modal-button--prev'),
    swipers: document.querySelectorAll<HTMLElement>('.swiper'),
  },

  init() {
    if (document.querySelector<HTMLElement>('.modal') && document.querySelectorAll<HTMLElement>('[data-modal]')) {
      document.querySelectorAll<HTMLButtonElement>('[data-modal]').forEach((item: HTMLButtonElement) => {
        // These triggers can be added dynamically, so we need to set the listeners like this.
        // This needs to be invoked after the triggers have been added to the DOM.
        // import this file then call modal.init() after the elements have been added to the DOM.
        this.setListeners(item.dataset.modal)
        this.setDynamicListeners(item)
      })
    }
  },

  // Open the modal
  async open(type: string, extras: ModalExtras = {}) {
    let imageSrc = ''

    if (type === 'image') {
      imageSrc = (document.querySelector<HTMLElement>('.swiper-slide-active[data-image-src]') as HTMLElement).dataset
        .imageSrc

      if (imageSrc === '' || imageSrc === undefined) {
        imageSrc = (document.querySelector<HTMLElement>('.swiper-slide-active[data-arurl]') as HTMLElement).dataset
          .imageSrc
      }

      extras = { ...extras, imageSrc }
    }

    // Show the loader
    this.handleLoader(true)

    // Wait for the close procedure to finish.
    await this.close(type)

    // Set data-type attribute to the modal
    this.el.root.setAttribute('data-type', type)

    // Add the type to the modal
    if (type !== 'image') {
      this.el.root.classList.add(`modal--${type}`)
    }

    // Add the modal-open class to the body
    this.el.body.classList.add('modal-open')

    // Add the content to the modal
    await this.handleContent(type, extras)

    if (type === 'image') {
      this.el.close.style.display = 'none'
      if (extras.swiper) {
        extras.swiper.classList.add('modal--swiper--active')
      }
      if (!extras.swiper.parentElement.classList.contains('image-gallery-block--single-slide')) {
        this.el.next.classList.remove('hidden')
        this.el.prev.classList.remove('hidden')
      }
      this.setupNavigationListeners()
    } else {
      // Hide the loader when the content has been added
      this.handleLoader()
    }
  },

  // Close the modal
  close(type: string) {
    this.el.body.classList.remove('modal-open')
    this.el.root.classList.remove(`modal--${type}`)
    this.el.next.classList.add('hidden')
    this.el.prev.classList.add('hidden')
    // TODO: Add timeout to prevent flickering?
    this.removeType(document.querySelectorAll<HTMLElement>('.modal'))
    document.querySelector('.modal--swiper--active')?.classList.remove('modal--swiper--active')
    if (type === 'image') {
      this.el.root.classList.remove('modal--image')
    }
    // Reset the content
    this.el.content.innerHTML = ''
  },

  // Clear the data-type attribute value from the modal
  removeType(items: NodeListOf<HTMLElement>) {
    items.forEach((item: HTMLElement) => {
      item.removeAttribute('data-type')
    })
  },
  setupNavigationListeners() {
    if (this.el.next && this.el.prev) {
      this.el.next.addEventListener('click', () => this.navigateImages('next'))
      this.el.prev.addEventListener('click', () => this.navigateImages('prev'))
    }
  },
  navigateImages(direction) {
    const activeSwiper = document.querySelector('.modal--swiper--active') as HTMLElement
    // @ts-ignore
    const swiper = activeSwiper.swiper

    if (direction === 'next') {
      swiper.slideNext()
      this.handleLoader(true)
      this.el.root.classList.remove('modal--image')
    } else {
      swiper.slidePrev()
      this.handleLoader(true)
      this.el.root.classList.remove('modal--image')
    }

    const activeSlide = activeSwiper.querySelector('.swiper-slide-active') as HTMLElement

    if (activeSlide) {
      const dataset = activeSlide.dataset
      this.handleContent('image', {
        imageSrc: dataset.imageSrc,
        desktopString: dataset.imageDesktop,
        mobileString: dataset.imageMobile,
        culture: dataset.culture,
      })
    }
  },

  // Set listeners for the modal
  setListeners(type: string) {
    this.el.close.addEventListener('click', (e: Event) => {
      this.close(type)
      e.stopImmediatePropagation()
    })
    this.el.overlay.addEventListener('click', (e: Event) => {
      this.close(type)
      e.stopImmediatePropagation()
    })

    document.addEventListener('keyup', (e) => {
      if (e.keyCode === 27 || e.which === 27) {
        this.close(type)
      }
      e.stopImmediatePropagation()
    })
  },
  setDynamicListeners(item: HTMLButtonElement) {
    item.addEventListener('click', (e: Event & { target: HTMLButtonElement }) => {
      let datasetModal = (e.target.parentNode.parentNode as HTMLElement).dataset.modal
      const swiper = e.target.parentNode.parentNode.parentNode.parentNode as HTMLElement
      if (datasetModal === undefined) {
        datasetModal = (e.target.parentNode.parentNode.parentNode as HTMLElement).dataset.modal
      }

      let parent = e.target.parentNode.parentNode as HTMLElement

      if (parent.classList.contains('ar--image__wrapper')) {
        parent = e.target.parentNode.parentNode.parentNode as HTMLElement
      }

      if (
        e.target.classList.contains('sailing-schedule__table-port-name') ||
        e.target.classList.contains('sailing-schedule__line-voyage-date') ||
        e.target.classList.contains('sailing-schedule__line-voyage-date') ||
        e.target.classList.contains('sailing-schedule__line-table-head')
      ) {
        datasetModal = e.target.dataset.modal
      }

      this.open(datasetModal, {
        id: e.target.dataset.id,
        line: e.target.dataset.line,
        mobileString: parent.dataset.imageMobile,
        desktopString: parent.dataset.imageDesktop,
        culture: parent.dataset.culture,
        arUrl: parent.dataset.arurl,
        swiper: swiper,
      })

      // This prevents the click from happening multiple times.
      e.stopImmediatePropagation()
    })
  },

  // Handle the loader
  handleLoader(show = false) {
    if (show) {
      this.el.loader.style.display = 'flex'
      return
    }

    this.el.loader.style.display = 'none'
  },

  // Handle the content
  async handleContent(type, extras) {
    await modalContent.init(type, this.el.content, extras)
    if (type === 'image') {
      const img = document.querySelector<HTMLImageElement>('.modal__image')
      img.addEventListener('load', () => {
        this.handleLoader()
        this.el.close.style.display = 'flex'
        this.el.root.classList.add('modal--image')
      })
    }
  },

  //Handle image load
  async handleImageLoad() {},
}

export default modal
